import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from './ObjectId.js';

export const CursorPaginationValidationBaseSchema = z.object({
  limit: z.number().int().min(1).max(100).default(10).optional().meta({
    description: 'Number of records per page (integer, max 100, default 10).',
  }),
  starting_after: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Cursor-based pagination. Pass a record `id` to return results after it. Cannot be used with `ending_before`.',
  }),
  ending_before: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Cursor-based pagination. Pass a record `id` to return results before it. Cannot be used with `starting_after`.',
  }),
});

export const SellerAPICursorPaginationValidationSchema = z.object({
  limit: z
    .string()
    .optional()
    .refine(val => !val || /^\d+$/.test(val), 'must be a positive integer')
    .transform(val => (val ? parseInt(val, 10) : undefined))
    .pipe(
      z.number().min(1, 'Limit must be at least 1').max(100, 'Limit cannot exceed 100').default(20),
    )
    .openapi({
      example: 20,
      description:
        'The number of results per page. Minimum value is 1 and maximum is 100. Defaults to 20.',
    }),
  starting_after: ObjectIdStringValidationSchema.optional().openapi({
    example: '507f1f77bcf86cd799439011',
    description:
      'A cursor value specifying the id of a resource to start before. Retrieves items that appear after this cursor in the list. Cannot be used together with `ending_before`.',
  }),
  ending_before: ObjectIdStringValidationSchema.optional().openapi({
    example: '507f1f77bcf86cd799439012',
    description:
      'A cursor value specifying the id of a resource to end after. Retrieves items that appear before this cursor in the list. Cannot be used together with `starting_after`.',
  }),
});

export const withCursorPaginationValidation = <T extends z.ZodObject<any>>(schema: T) =>
  schema.refine(data => !(data.starting_after && data.ending_before), {
    message: 'Cannot use both starting_after and ending_before in the same request',
    path: ['starting_after', 'ending_before'],
  });
